import type { EDocumentType } from '@/modules/profile/hooks/usePlayerDocuments';
import { sendGAEvent } from '../utils';

export enum OtherEvents {
  LANGUAGE_SWITCHER = 'language_switcher',
  UPGRADE_VIP_BUTTON = 'upgrade_vip_button',
  SIGN_OUT_CLICK = 'sign_out_click',
  SIGN_OUT_WITHOUT_CLICK = 'sign_out_without_click',
  REFFERAL_PROGRAM_COPY_LINK = 'referral_program_copy_link',
  KYC_UPLOAD_RESULT = 'kyc_upload_result',
  MODAL_GAME_ADD_DATA_SUBMIT = 'modal_game_add_data_submit',
  MODAL_GAME_ADD_DATA_APPEARANCE = 'modal_game_add_data_appearance',
  PROMO_NDB_CODE_APPLY = 'promo_ndb_code_apply',
  AB_TEST_ASSIGNMENT = 'ab_test_assignment',
}

interface IKYSUploadResultEvent {
  status: 'success' | 'failed';
  section: EDocumentType;
  format_type: string;
  error_data?: string;
}

interface ILanguageSwitcherEvent {
  type: 'footer' | 'header';
  from: string;
  to: string;
  country_code: string;
  browser_language: string;
}

interface IPromoNDBCodeApply {
  code: string;
  code_sent_to_back: string;
  status: 'success' | 'failed';
  error_data?: string;
}

interface IABTestAssignmentEvent {
  test_name: string;
  test_version: string;
  test_group: string;
}

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user change language manually.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `language_switcher` and the category is `other`.
 * The data object contains the type, from, to, country_code, browser_language
 */
export const sendOtherLanguageSwitcherEvent = (
  data: ILanguageSwitcherEvent
) => {
  sendGAEvent(OtherEvents.LANGUAGE_SWITCHER, {
    event_category: 'other',
    ...data,
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on upgrade button.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `upgrade_vip_button` and the category is `other`.
 */
export const sendOtherUpgradeVipButtonEvent = () => {
  sendGAEvent(OtherEvents.UPGRADE_VIP_BUTTON, {
    event_category: 'other',
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on sign out.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `sign_out_click` and the category is `other`.
 */
export const sendOtherSignOutClickEvent = () => {
  sendGAEvent(OtherEvents.SIGN_OUT_CLICK, {
    event_category: 'other',
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user passivly logged out.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `sign_out_without_click` and the category is `other`.
 */
export const sendOtherSignOutWithoutClickEvent = () => {
  sendGAEvent(OtherEvents.SIGN_OUT_WITHOUT_CLICK, {
    event_category: 'other',
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user copy refferal program link
 * from refferal program page.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `referral_program_copy_link` and the category is `other`.
 */
export const sendOtherRefferalProgramCopyLinkEvent = () => {
  sendGAEvent(OtherEvents.REFFERAL_PROGRAM_COPY_LINK, {
    event_category: 'other',
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on submit button on
 * the gender popup.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `gender_popup_submit` and the category is `other`.
 */
export const sendOtherModalGameAddDataSubmitEvent = (fields: string[]) => {
  sendGAEvent(OtherEvents.MODAL_GAME_ADD_DATA_SUBMIT, {
    event_category: 'other',
    fields: fields.join('/'),
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user click on submit button on
 * the gender popup.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `gender_popup_submit` and the category is `other`.
 */
export const sendOtherModalGameAddDataAppearanceEvent = (fields: string[]) => {
  sendGAEvent(OtherEvents.MODAL_GAME_ADD_DATA_APPEARANCE, {
    event_category: 'other',
    fields: fields.join('/'),
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user trying to upload KYC document.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `kyc_upload_result` and the category is `other`.
 * The data object contains the status, section, format_type, error_data
 */
export const sendOtherKYCUlploadResultEvent = (data: IKYSUploadResultEvent) => {
  sendGAEvent(OtherEvents.KYC_UPLOAD_RESULT, {
    event_category: 'other',
    ...data,
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when user trying to upload KYC document.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `promo_ndb_code_apply` and the category is `other`.
 * The data object contains the code, status, error_data
 */
export const sendOtherPromoNDBCodeApplyEvent = (data: IPromoNDBCodeApply) => {
  sendGAEvent(OtherEvents.PROMO_NDB_CODE_APPLY, {
    event_category: 'other',
    ...data,
  });
};

/**
 * @param data - The data to be sent with the event.
 * @description This function sends a Google Analytics event when a user is assigned to an AB test.
 * It uses the `sendGAEvent` utility to send the event with the specified event name and category.
 * The event name is `ab_test_assignment` and the category is `other`.
 * The data object contains the test_name, test_version, test_group
 */
export const sendOtherABTestAssignmentEvent = (
  data: IABTestAssignmentEvent
) => {
  sendGAEvent(OtherEvents.AB_TEST_ASSIGNMENT, {
    event_category: 'other',
    ...data,
  });
};
